import React, { useCallback, useEffect, useMemo } from "react";
import { mapDoctor, mapDoctorInstance } from "../../helpers";
import { DoctorUser, BPDoctor } from "../../models";
import isEqual from "lodash/isEqual";
import { reducer, State } from "./DoctorEditReducer";
import { useMsal } from "@azure/msal-react";
import { useUserContext } from "../../contexts";
import { apiAccessRequest, DOCTOR_TABLE_PAGE_SIZE } from "../../constants";

export const useDoctorEdit = (
	doctorUserFromLocation: DoctorUser | undefined
) => {
	const { instance } = useMsal();
	const { account } = useUserContext();

	const initialState: State = {
		originalUser: null,
		originalDoctor: null,
		user: null,
		doctor: null,
		availableBpDoctors: [],
		errors: [],
		isUpdating: false
	};

	const [state, dispatch] = React.useReducer(reducer, initialState);

	const consultsUrl = process.env.REACT_APP_CONSULTS_URL ?? "";

	const fetchDoctor = useCallback(
		async (id: number) => {
			const tokenResult = await instance
				.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				})
				.catch(() => null);
			const headers: any = {};
			if (tokenResult) {
				headers.Authorization = `Bearer ${tokenResult.accessToken}`;
			}
			const result = await fetch(`${consultsUrl}Doctor/${id}`, {
				headers
			});
			if (!result.ok) throw new Error("fetch doctor failed");
			const doctor = await result.json();
			return mapDoctor(doctor);
		},
		[account, consultsUrl, instance]
	);

	const fetchDoctorInstances = useCallback(
		async (
			clinicId: string,
			searchTerm: string,
			hideLinked: boolean,
			page = 1
		) => {
			const tokenResult = await instance
				.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				})
				.catch(() => null);
			const headers: any = {};
			if (tokenResult) {
				headers.Authorization = `Bearer ${tokenResult.accessToken}`;
			}
			const query = new URLSearchParams({
				searchTerm,
				includeLinked: String(!hideLinked),
				Page: String(page),
				Take: String(DOCTOR_TABLE_PAGE_SIZE)
			});
			if (clinicId) query.set("clinicId", clinicId);
			const result = await fetch(
				`${consultsUrl}Doctor/DoctorInstances?${query}`,
				{
					headers
				}
			);
			if (!result.ok) throw new Error("fetch instances failed");
			const data = await result.json();
			return (data.value as BPDoctor[]).map(mapDoctorInstance);
		},
		[account, consultsUrl, instance]
	);

	const init = useCallback(async () => {
		if (!doctorUserFromLocation) return;
		const user = doctorUserFromLocation;

		try {
			let doctor;

			if (user.doctor?.id) {
				doctor = await fetchDoctor(user.doctor.id);
			} else {
				doctor = {
					id: 0,
					upn: user.upn,
					salutation: "",
					firstName: "",
					lastName: "",
					fullName: "",
					displayName: "",
					sex: "",
					qualifications: "",
					prescriberNumber: "",
					takingPatients: false,
					takingPatientsTelehealth: false,
					stopTakingPatientCount: 0,
					dailyLimit: 0,
					lthDailyCap: null,
					isAssignable: false,
					available: false,
					availableTelehealth: false,
					status: true,
					doctorInstances: [],
					bpDoctors: []
				};
			}

			dispatch({
				type: "init",
				payload: { user, doctor, bpInstances: [] }
			});
		} catch (e) {
			console.error(e);
			throw e;
		}
	}, [doctorUserFromLocation, fetchDoctor]);

	useEffect(() => {
		if (doctorUserFromLocation) {
			init().catch((e) => console.error(e));
		}
	}, [doctorUserFromLocation, init]);

	const setField = (path: string[], value: any) =>
		dispatch({ type: "set_field", payload: { path, value } });
	const linkBPDoctor = (bp: BPDoctor) =>
		dispatch({ type: "link_bp", payload: bp });
	const unlinkBPDoctor = (id: number) =>
		dispatch({ type: "unlink_bp", payload: id });
	const toggleConsult = (
		id: number,
		type: "FaceToFace" | "Telehealth" | "LocalTelehealth" | "TakingAppointments"
	) => dispatch({ type: "toggle_consult", payload: { id, type } });
	const setErrors = (errs: number[]) =>
		dispatch({ type: "set_errors", payload: errs });
	const setUpdating = (val: boolean) =>
		dispatch({ type: "set_updating", payload: val });

	const hasChanges = useMemo(() => {
		if (!state.originalDoctor || !state.doctor) return false;
		return !isEqual(state.originalDoctor, state.doctor);
	}, [state.originalDoctor, state.doctor]);

	return {
		state,
		fetchDoctorInstances,
		init,
		setField,
		linkBPDoctor,
		unlinkBPDoctor,
		toggleConsult,
		setErrors,
		setUpdating,
		hasChanges
	};
};
